import { isSunday, subDays, isMonday } from 'date-fns'
import { UseFormSetError } from 'react-hook-form'
import {
  CreationType,
  ResponseCreateAnyCreations,
  ResponseCreationContentBanner,
  ResponseCreationContentNative,
  ResponseCreationContentRichMedia,
  ResponseCreationContentSgNative,
  ResponseCreationNativeLink,
  ResponseCreationWithPreviewUrls,
  RequestCreation,
  RequestCreationUpdate,
} from '@ads/front-core/api'
import { Campaign, TCreationVariant } from '@ads/front-core/interfaces'
import { getWidthHeight, getLocateDate, parseDate } from '@ads/front-core/utils'
import {
  IPreviewCreation,
  PreviewMethod,
} from '@ads/front-core/components/CreationPreview'
import { lng } from '@ads/front-core/locales'
import { INCORRECT_TARGET_URL_CODE } from '@ads/front-core/validation'
import { creationVariantsOptions } from './nativeCampaignCreationVariants'

type GenerateMetodAttrs = ({
  campaignId,
  creation,
  creationVariants,
}: {
  campaignId: Campaign['id']
  creation: ResponseCreationWithPreviewUrls
  creationVariants: TCreationVariant[]
}) => IPreviewCreation[]

const simple: GenerateMetodAttrs = ({ creation, campaignId }) => [
  {
    key: `${creation.id}-simple`,
    creationId: creation.id,
    name: creation.name,
    previewMethod: PreviewMethod.IFRAME_URL,
    url: `/api/campaign/${campaignId}/preview?noleap&creationId=${creation.id}`,
  },
]

const simpleWithPreview: GenerateMetodAttrs = ({ creation, campaignId }) => {
  const content = creation.content as ResponseCreationContentBanner
  return [
    {
      key: `${creation.id}-simple-preview`,
      creationId: creation.id,
      label: creation.name,
      previewMethod: PreviewMethod.IFRAME_URL,
      url: `/api/campaign/${campaignId}/preview?noleap&creationId=${creation.id}`,
      size: content.size,
      previewExternalUrl: Object.values(creation.previewUrls || {})?.[0],
    },
  ]
}

const mailing: GenerateMetodAttrs = ({ creation, campaignId }) => [
  {
    key: `${creation.id}-mailing`,
    creationId: creation.id,
    name: creation.name,
    previewMethod: PreviewMethod.IFRAME_URL,
    url: `/api/campaign/${campaignId}/preview?noleap&creationId=${creation.id}`,
    isFullWindowSize: true,
  },
]

const native: GenerateMetodAttrs = ({
  campaignId,
  creation,
  creationVariants,
}) => {
  const creations: IPreviewCreation[] = []
  const content = creation.content as ResponseCreationContentNative
  creationVariantsOptions.forEach(creationVariant => {
    const variant = creationVariant.variant

    const activeVariants =
      creationVariants?.find(variant => variant.creationId === creation.id)
        ?.variants || content.activeVariants

    if (activeVariants[variant]) {
      const url = `/api/campaign/${campaignId}/preview?noleap&creationId=${creation.id}&creationVariant=${variant}`
      creations.push({
        key: `${creation.id}-${variant}`,
        creationId: creation.id,
        label: creationVariant.name,
        previewMethod: PreviewMethod.IFRAME_URL,
        url,
        previewExternalUrl: creation.previewUrls[variant],
        size: creationVariant.size,
      })
    }
  })
  return creations
}

const richMedia: GenerateMetodAttrs = ({ creation }) => {
  const content = creation.content as ResponseCreationContentRichMedia
  return [
    {
      key: `${creation.id}-rich-media`,
      creationId: creation.id,
      label: creation.name,
      previewMethod: PreviewMethod.FETCH_HTML,
      requestBody: {
        type: creation.content.type,
        content: {
          subtype: content.subtype,
          media: content.media,
          ...getWidthHeight(content.size),
        },
      },
      previewExternalUrl: creation.previewUrls.rich_media,
    },
  ]
}

const nativeLink: GenerateMetodAttrs = ({ creation }) => {
  const content = creation.content as ResponseCreationNativeLink
  return [
    {
      key: `${creation.id}-native-link`,
      creationId: creation.id,
      label: creation.name,
      previewMethod: PreviewMethod.NATIVE_LINK,
      content: {
        type: creation.content.type,
        clientName: content.clientName,
        topic: content.topic,
        callToAction: content.callToAction,
      },
    },
  ]
}

const nativeSg: GenerateMetodAttrs = ({ creation }) => {
  const content = creation.content as ResponseCreationContentSgNative
  return [
    {
      key: `${creation.id}-native-sg`,
      creationId: creation.id,
      label: creation.name,
      previewMethod: PreviewMethod.NATIVE_SG,
      content: {
        src: content.src,
        targetUrl: content.targetUrl,
        text: content.text,
      },
      previewExternalUrl: creation.previewUrls.native_sg,
    },
  ]
}

const GenerateCreationPreview: Partial<
  Record<CreationType, GenerateMetodAttrs>
> = {
  MAILING_DYNAMIC: mailing,
  STUDIO: simple,
  BANNER: simpleWithPreview,
  CODE: simpleWithPreview,
  HTML: simpleWithPreview,
  NATIVE: native,
  RICH_MEDIA: richMedia,
  NATIVE_LINK_DIRECT: nativeLink,
  NATIVE_SG: nativeSg,
  OUTSTREAM_VIDEO: simpleWithPreview,
}

export const generateCreationsPreview = ({
  campaignId,
  creations,
  creationVariants,
}: {
  campaignId: Campaign['id']
  creations: ResponseCreationWithPreviewUrls[]
  creationVariants?: TCreationVariant[]
}): IPreviewCreation[] => {
  const previewCreations: IPreviewCreation[] = []

  creations.forEach(creation => {
    const generateCreationPreviewMethod =
      GenerateCreationPreview[creation.content.type]
    if (typeof generateCreationPreviewMethod === 'function') {
      const previews = generateCreationPreviewMethod({
        campaignId,
        creation,
        creationVariants,
      })
      previews?.forEach(preview => previewCreations.push(preview))
    }
  })

  return previewCreations
}

export const getDeadlineDateForCreations = (
  selectedDate?: string | Date,
): string => {
  const futureDate = getDeadlineDateForCreationsDate(selectedDate)
  return getLocateDate(futureDate)
}

export const getDeadlineDateForCreationsDate = (
  selectedDate?: string | Date,
): Date => {
  let futureDate =
    selectedDate instanceof Date
      ? selectedDate
      : typeof selectedDate === 'string' && selectedDate
      ? parseDate(selectedDate)
      : new Date()

  futureDate.setHours(12)
  futureDate.setMinutes(0)

  if (isSunday(futureDate)) {
    futureDate = subDays(futureDate, 2)
  } else if (isMonday(futureDate)) {
    futureDate = subDays(futureDate, 3)
  } else {
    futureDate = subDays(futureDate, 1)
  }

  return futureDate
}

export const checkTargetUrlErrors = (
  requestBody: RequestCreation[] | RequestCreationUpdate[],
  response: ResponseCreateAnyCreations,
  setError: UseFormSetError<any>,
  shopUrl: string,
): boolean => {
  let hasTargetUrlError = false

  response?.forEach(item => {
    if (item?.error?.includes(INCORRECT_TARGET_URL_CODE)) {
      setError('targetUrl', {
        message: lng('errors:targetUrlWrongDomainError', {
          domain: shopUrl,
        }),
      })
      hasTargetUrlError = true
    }
    if (item?.error?.includes('InternalValidationError') && item?.message) {
      setError('file', { message: item.message })
      hasTargetUrlError = true
    }
    if (!hasTargetUrlError && item?.error && !item.message) {
      setError('file', { message: lng('errors:tryAgain') })
      hasTargetUrlError = true
    }
  })

  return hasTargetUrlError
}
